/**
 * AvailabilityEditorCore — shared presentational component for the weekly-window +
 * optional blackout-date editor. Consumed by:
 *   - BookingManager.tsx  AvailabilityEditor  (admin; no inline blackouts, save → bookingApi.setAvailability)
 *   - PublicAvailabilityPage.tsx              (public; blackouts included, save → bookingApi.publicAvailabilitySet)
 *
 * CRITICAL: day_of_week 0=Monday … 6=Sunday — matches booking_engine (d.weekday()) and the
 * admin AvailabilityEditor DAYS array. Do NOT change this convention.
 */
import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import GlassButton from '../ui/GlassButton';

// 0=Monday … 6=Sunday — MUST match booking_engine d.weekday()
export const AVAIL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
// Short labels used by the admin editor
export const AVAIL_DAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export interface AvailabilityRow {
  day_of_week: number; // 0=Monday … 6=Sunday
  start_time: string;  // "HH:MM"
  end_time: string;    // "HH:MM"
}

export interface AvailabilityEditorCoreProps {
  /** Full label for each day (e.g. 'Monday') vs short (e.g. 'Mon'). Default: short. */
  dayLabels?: 'long' | 'short';
  rows: AvailabilityRow[];
  onRowsChange: (rows: AvailabilityRow[]) => void;
  /** When provided, renders the blackout-date section. Omit to hide it (admin editor). */
  blackoutDates?: string[];
  onBlackoutChange?: (dates: string[]) => void;
}

const AvailabilityEditorCore: React.FC<AvailabilityEditorCoreProps> = ({
  dayLabels = 'short',
  rows,
  onRowsChange,
  blackoutDates,
  onBlackoutChange,
}) => {
  const [newBlackout, setNewBlackout] = useState('');
  const labels = dayLabels === 'long' ? AVAIL_DAYS : AVAIL_DAYS_SHORT;
  const showBlackouts = blackoutDates !== undefined && onBlackoutChange !== undefined;

  const addRow = (day: number) => {
    onRowsChange([...rows, { day_of_week: day, start_time: '09:00', end_time: '17:00' }]);
  };

  const removeRow = (idx: number) => {
    onRowsChange(rows.filter((_, i) => i !== idx));
  };

  const updateRow = (idx: number, field: keyof AvailabilityRow, value: string | number) => {
    onRowsChange(rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addBlackout = () => {
    if (!newBlackout || !onBlackoutChange || !blackoutDates) return;
    if (!blackoutDates.includes(newBlackout)) {
      onBlackoutChange([...blackoutDates, newBlackout].sort());
    }
    setNewBlackout('');
  };

  const removeBlackout = (d: string) => {
    if (!onBlackoutChange || !blackoutDates) return;
    onBlackoutChange(blackoutDates.filter((x) => x !== d));
  };

  return (
    <>
      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
        Weekly Availability
      </div>

      {labels.map((label, dayIdx) => {
        const dayRows = rows.map((r, i) => ({ r, i })).filter(({ r }) => r.day_of_week === dayIdx);
        return (
          <div key={dayIdx} className="bkm-avail-day">
            <div className="bkm-avail-day-label">{label}</div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {dayRows.length === 0 && (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>Unavailable</span>
              )}
              {dayRows.map(({ r, i }) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <input
                    type="time"
                    className="bkm-time-input"
                    value={r.start_time}
                    onChange={(e) => updateRow(i, 'start_time', e.target.value)}
                  />
                  <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>–</span>
                  <input
                    type="time"
                    className="bkm-time-input"
                    value={r.end_time}
                    onChange={(e) => updateRow(i, 'end_time', e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '4px' }}
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addRow(dayIdx)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  fontSize: 'var(--text-xs)', color: 'var(--primary)',
                  background: 'none', border: '1px dashed var(--stroke)',
                  borderRadius: 'var(--radius-sm)', padding: '2px 8px',
                  cursor: 'pointer', width: 'fit-content',
                }}
              >
                <Plus size={12} /> Add window
              </button>
            </div>
          </div>
        );
      })}

      {showBlackouts && (
        <div style={{ borderTop: '1px solid var(--stroke)', paddingTop: 'var(--space-3)' }}>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', marginBottom: 'var(--space-2)' }}>
            Blackout Dates
          </div>
          {blackoutDates!.map((d) => (
            <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{d}</span>
              <button
                type="button"
                onClick={() => removeBlackout(d)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <input
              type="date"
              className="bkm-time-input"
              value={newBlackout}
              onChange={(e) => setNewBlackout(e.target.value)}
            />
            <GlassButton type="button" variant="secondary" onClick={addBlackout}>
              <Plus size={14} /> Add
            </GlassButton>
          </div>
        </div>
      )}
    </>
  );
};

export default AvailabilityEditorCore;
